import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  Eye,
  MessageCircle,
  ShieldAlert,
  ShieldCheck,
  Star,
  UserRound,
} from 'lucide-react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import {
  Avatar,
  BottomSheet,
  Button,
  Card,
  CardContent,
  Loading,
  Modal,
  Textarea,
} from '@/components/ui';
import { ReviewComposerBottomSheet } from '@/components/reviews/ReviewComposerBottomSheet';
import { trackEvent } from '@/lib/analytics';
import { useAuthStore } from '@/stores/authStore';
import { useAccessRequestStore, type AccessRequestStatus } from '@/stores/accessRequestStore';
import { useMessageStore } from '@/stores/messageStore';
import { useReviewStore, type ReviewableConversation } from '@/stores/reviewStore';
import { useToastStore } from '@/stores/toastStore';
import { DONATION_METHODS, formatAgeRange, type UserRole, type UserStatus } from '@kulum/shared';

interface ProfileBottomSheetProps {
  userId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onOpenConversation?: (conversationId: string) => void;
}

interface ProfileDetailData {
  userId: string;
  displayName: string;
  iconUrl?: string;
  role?: UserRole;
  status?: UserStatus;
  identityVerified?: boolean;
  basicInfo?: {
    birthDate?: Date;
    nationality?: string;
    bloodType?: string;
    bloodTypeRh?: string;
    birthplace?: string;
    residence?: string;
    education?: {
      level?: string;
      schoolName?: string;
      faculty?: string;
      status?: string;
    };
    annualIncome?: string;
    maritalStatus?: string;
    children?: {
      sons: number;
      daughters: number;
    };
    ancestry?: string;
  };
  physicalInfo?: {
    height?: number;
    weight?: number;
    bodyType?: string;
    hairType?: string;
    hairColor?: string;
    eyelid?: string;
  };
  donationPolicy?: {
    methods?: string[];
    compensation?: number;
    travelExpense?: string;
    identityDisclosure?: string;
    consanguinityMeasures?: string;
    rightToKnowOrigin?: string;
    sameCityFamilyDonation?: string;
    donationAreas?: string[];
  };
  doneePolicy?: {
    methods?: string[];
    preferredLocation?: string;
  };
  verificationBadges?: {
    identity?: boolean;
    education?: boolean;
    income?: boolean;
    spermTest?: boolean;
    stdTest?: boolean;
    ranks?: {
      spermTest?: string;
    };
  };
  freeText?: string;
  images: string[];
}

function toDate(value: unknown): Date | undefined {
  if (
    typeof value === 'object' &&
    value !== null &&
    'toDate' in value &&
    typeof (value as { toDate: unknown }).toDate === 'function'
  ) {
    return (value as { toDate: () => Date }).toDate();
  }
  return undefined;
}

function calculateAge(birthDate?: Date): number | null {
  if (!birthDate) return null;
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

function getDonationMethodLabel(method: string): string {
  const found = DONATION_METHODS.find((item) => item.value === method);
  return found?.label || method;
}

const UNSET_LABEL = '未設定';

function displayText(value?: string | null): string {
  return value && value.trim().length > 0 ? value : UNSET_LABEL;
}

function displayNumber(value: number | undefined, unit: string): string {
  return typeof value === 'number' ? `${value}${unit}` : UNSET_LABEL;
}

function displayBloodType(bloodType?: string, bloodTypeRh?: string): string {
  if (!bloodType && !bloodTypeRh) {
    return UNSET_LABEL;
  }

  const parts = [];
  if (bloodType) {
    parts.push(`${bloodType}型`);
  }
  if (bloodTypeRh) {
    parts.push(bloodTypeRh === 'unknown' ? 'Rh不明' : `Rh${bloodTypeRh}`);
  }
  return parts.join(' / ');
}

export function ProfileBottomSheet({ userId, isOpen, onClose, onOpenConversation }: ProfileBottomSheetProps) {
  const navigate = useNavigate();
  const authUser = useAuthStore((state) => state.user);
  const userData = useAuthStore((state) => state.userData);
  const addToast = useToastStore((state) => state.addToast);

  const findOrCreateConversation = useMessageStore((state) => state.findOrCreateConversation);
  const conversations = useMessageStore((state) => state.conversations);
  const sendRequest = useAccessRequestStore((state) => state.sendRequest);
  const checkRequestStatus = useAccessRequestStore((state) => state.checkRequestStatus);
  const receivedReviews = useReviewStore((state) => state.receivedReviews);
  const receivedStats = useReviewStore((state) => state.receivedStats);
  const reviewableConversations = useReviewStore((state) => state.reviewableConversations);
  const isLoadingReceivedReviews = useReviewStore((state) => state.isLoadingReceived);
  const isLoadingReviewableConversations = useReviewStore((state) => state.isLoadingReviewable);
  const isSubmittingReview = useReviewStore((state) => state.isSubmitting);
  const reviewError = useReviewStore((state) => state.error);
  const fetchReceivedReviews = useReviewStore((state) => state.fetchReceivedReviews);
  const fetchReviewableConversations = useReviewStore((state) => state.fetchReviewableConversations);
  const submitReview = useReviewStore((state) => state.submitReview);
  const clearReviewError = useReviewStore((state) => state.clearError);

  const [profile, setProfile] = useState<ProfileDetailData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestStatus, setRequestStatus] = useState<AccessRequestStatus | null>(null);
  const [isCheckingRequest, setIsCheckingRequest] = useState(false);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [requestMessage, setRequestMessage] = useState('');
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [isStartingChat, setIsStartingChat] = useState(false);
  const [reviewStats, setReviewStats] = useState<{ averageRating: number; totalCount: number }>({ averageRating: 0, totalCount: 0 });
  const [isReviewsViewOpen, setIsReviewsViewOpen] = useState(false);
  const [isReviewComposerOpen, setIsReviewComposerOpen] = useState(false);
  const [selectedReviewConversation, setSelectedReviewConversation] = useState<ReviewableConversation | null>(null);
  const [reviewRating, setReviewRating] = useState<1 | 2 | 3 | 4 | 5>(5);
  const [reviewComment, setReviewComment] = useState('');

  const isOwnProfile = !!authUser?.uid && authUser.uid === userId;
  const viewerRole = userData?.role;
  const viewerIdentityVerified = userData?.identityVerified === true;
  const isDoneeWithoutIdentity = viewerRole === 'donee' && !viewerIdentityVerified;
  const targetDonorIsApproved = profile?.role === 'donor' && profile?.status === 'approved';
  
  // 閲覧申請ボタンの表示条件と有効化条件を分離
  const showRequestButton = !isOwnProfile && viewerRole === 'donee' && profile?.role === 'donor';
  const isRequestButtonEnabled = 
    showRequestButton && 
    targetDonorIsApproved && 
    viewerIdentityVerified && 
    !isCheckingRequest &&
    requestStatus !== 'pending' &&    // 申請中は非活性
    requestStatus !== 'approved';     // 承認済みは非活性（拒否済みは活性化）
  
  const canStartChat = (() => {
    if (isOwnProfile || !profile) return false;
    if (viewerRole === 'donee' && profile.role === 'donor') {
      return targetDonorIsApproved;  // approved のみ
    }
    if (viewerRole === 'donor' && profile.role === 'donee') {
      return true;  // ドナー→ドニーは常にOK
    }
    return false;  // その他は不可
  })();
  const reviewableConversationForTarget = useMemo(
    () =>
      reviewableConversations.find((conversation) => conversation.otherUserId === userId) ?? null,
    [reviewableConversations, userId]
  );
  const canWriteReviewForTarget =
    !!authUser?.uid && !isOwnProfile && !isDoneeWithoutIdentity && !!reviewableConversationForTarget;
  const reviewWriteButtonLabel = useMemo(() => {
    if (isLoadingReviewableConversations) return '確認中...';
    if (isDoneeWithoutIdentity) return '本人確認後に投稿可能';
    return 'レビューを書く';
  }, [isDoneeWithoutIdentity, isLoadingReviewableConversations]);
  const distributionRows = useMemo(() => [5, 4, 3, 2, 1] as const, []);
  const age = useMemo(() => calculateAge(profile?.basicInfo?.birthDate), [profile?.basicInfo?.birthDate]);

  useEffect(() => {
    const loadProfile = async () => {
      if (!userId || !isOpen) return;

      try {
        setIsLoading(true);
        setError(null);
        setProfile(null);
        setRequestStatus(null);
        setRequestMessage('');
        setIsReviewsViewOpen(false);
        setIsReviewComposerOpen(false);
        setSelectedReviewConversation(null);
        setReviewRating(5);
        setReviewComment('');
        clearReviewError();

        const userRef = doc(db, 'users', userId);
        const profileRef = doc(db, 'profiles', userId);
        const privateProfileRef = doc(db, 'privateProfiles', userId);

        const [userSnap, profileSnap] = await Promise.all([
          getDoc(userRef),
          getDoc(profileRef),
        ]);

        if (!userSnap.exists()) {
          setError('プロフィールが見つかりませんでした');
          return;
        }

        const userDoc = userSnap.data() as {
          displayName?: unknown;
          iconUrl?: unknown;
          role?: unknown;
          status?: unknown;
          identityVerified?: unknown;
        };

        const profileDoc = profileSnap.exists()
          ? (profileSnap.data() as {
              basicInfo?: {
                birthDate?: unknown;
                nationality?: unknown;
                bloodType?: unknown;
                bloodTypeRh?: unknown;
                birthplace?: unknown;
                residence?: unknown;
                education?: {
                  level?: unknown;
                  schoolName?: unknown;
                  faculty?: unknown;
                  status?: unknown;
                };
                annualIncome?: unknown;
                maritalStatus?: unknown;
                children?: {
                  sons?: unknown;
                  daughters?: unknown;
                };
                ancestry?: unknown;
              };
              physicalInfo?: ProfileDetailData['physicalInfo'];
              donationPolicy?: ProfileDetailData['donationPolicy'];
              doneePolicy?: ProfileDetailData['doneePolicy'];
              verificationBadges?: {
                identity?: unknown;
                education?: unknown;
                income?: unknown;
                spermTest?: unknown;
                stdTest?: unknown;
                ranks?: {
                  spermTest?: unknown;
                };
              };
              freeText?: unknown;
              images?: unknown;
            })
          : {};

        let privateDoc: { freeText?: unknown; images?: unknown } = {};
        try {
          const privateSnap = await getDoc(privateProfileRef);
          if (privateSnap.exists()) {
            privateDoc = privateSnap.data() as { freeText?: unknown; images?: unknown };
          }
        } catch {
          // Permission denied means "private profile not visible" for this viewer.
        }

        const basicInfo = profileDoc.basicInfo || {};
        const verificationBadges = profileDoc.verificationBadges
          ? {
              identity: profileDoc.verificationBadges.identity === true,
              education: profileDoc.verificationBadges.education === true,
              income: profileDoc.verificationBadges.income === true,
              spermTest: profileDoc.verificationBadges.spermTest === true,
              stdTest: profileDoc.verificationBadges.stdTest === true,
              ranks: {
                spermTest:
                  typeof profileDoc.verificationBadges.ranks?.spermTest === 'string'
                    ? profileDoc.verificationBadges.ranks?.spermTest
                    : undefined,
              },
            }
          : undefined;
        const imagesFromPrivate = Array.isArray(privateDoc.images)
          ? privateDoc.images.filter((image): image is string => typeof image === 'string')
          : null;

        const nextProfile: ProfileDetailData = {
          userId,
          displayName: typeof userDoc.displayName === 'string' ? userDoc.displayName : '不明なユーザー',
          iconUrl: typeof userDoc.iconUrl === 'string' ? userDoc.iconUrl : undefined,
          role: (userDoc.role === 'donor' || userDoc.role === 'donee') ? userDoc.role : undefined,
          status: typeof userDoc.status === 'string' ? userDoc.status as UserStatus : undefined,
          identityVerified: userDoc.identityVerified === true,
          basicInfo: {
            birthDate: toDate(basicInfo.birthDate),
            nationality: typeof basicInfo.nationality === 'string' ? basicInfo.nationality : undefined,
            bloodType: typeof basicInfo.bloodType === 'string' ? basicInfo.bloodType : undefined,
            bloodTypeRh: typeof basicInfo.bloodTypeRh === 'string' ? basicInfo.bloodTypeRh : undefined,
            birthplace: typeof basicInfo.birthplace === 'string' ? basicInfo.birthplace : undefined,
            residence: typeof basicInfo.residence === 'string' ? basicInfo.residence : undefined,
            education: basicInfo.education
              ? {
                  level: typeof basicInfo.education.level === 'string' ? basicInfo.education.level : undefined,
                  schoolName: typeof basicInfo.education.schoolName === 'string' ? basicInfo.education.schoolName : undefined,
                  faculty: typeof basicInfo.education.faculty === 'string' ? basicInfo.education.faculty : undefined,
                  status: typeof basicInfo.education.status === 'string' ? basicInfo.education.status : undefined,
                }
              : undefined,
            annualIncome: typeof basicInfo.annualIncome === 'string' ? basicInfo.annualIncome : undefined,
            maritalStatus: typeof basicInfo.maritalStatus === 'string' ? basicInfo.maritalStatus : undefined,
            children: basicInfo.children
              ? {
                  sons: typeof basicInfo.children.sons === 'number' ? basicInfo.children.sons : 0,
                  daughters: typeof basicInfo.children.daughters === 'number' ? basicInfo.children.daughters : 0,
                }
              : undefined,
            ancestry: typeof basicInfo.ancestry === 'string' ? basicInfo.ancestry : undefined,
          },
          physicalInfo: profileDoc.physicalInfo,
          donationPolicy: profileDoc.donationPolicy,
          doneePolicy: profileDoc.doneePolicy,
          verificationBadges,
          // 自由記入欄は公開プロフィールで常時表示する
          freeText: typeof profileDoc.freeText === 'string' ? profileDoc.freeText : undefined,
          // 追加画像は privateProfiles のみ（閲覧申請承認者のみ表示）
          images: imagesFromPrivate ?? [],
        };

        setProfile(nextProfile);

        // レビュー統計を並列で取得
        try {
          const reviewsRef = collection(db, 'reviews');
          const reviewsQuery = query(reviewsRef, where('toUserId', '==', userId));
          const reviewsSnapshot = await getDocs(reviewsQuery);
          if (!reviewsSnapshot.empty) {
            let total = 0;
            reviewsSnapshot.docs.forEach((docSnap) => {
              const data = docSnap.data();
              const rating = typeof data.rating === 'number' ? data.rating : 0;
              total += rating;
            });
            const count = reviewsSnapshot.docs.length;
            setReviewStats({
              averageRating: Math.round((total / count) * 10) / 10,
              totalCount: count,
            });
          } else {
            setReviewStats({ averageRating: 0, totalCount: 0 });
          }
        } catch {
          setReviewStats({ averageRating: 0, totalCount: 0 });
        }
      } catch (fetchError) {
        console.error('Error loading profile detail:', fetchError);
        setError('プロフィールの取得に失敗しました');
      } finally {
        setIsLoading(false);
      }
    };

    void loadProfile();
  }, [userId, isOpen, clearReviewError]);

  useEffect(() => {
    const loadRequestStatus = async () => {
      if (!isOpen || !userId || !authUser?.uid || !showRequestButton) return;
      try {
        setIsCheckingRequest(true);
        const status = await checkRequestStatus(authUser.uid, userId);
        setRequestStatus(status);
      } finally {
        setIsCheckingRequest(false);
      }
    };

    if (showRequestButton) {
      void loadRequestStatus();
    } else {
      setRequestStatus(null);
      setIsCheckingRequest(false);
    }
  }, [isOpen, userId, authUser?.uid, showRequestButton, checkRequestStatus]);

  useEffect(() => {
    if (!isOpen || !isReviewsViewOpen || !userId) {
      return;
    }

    void fetchReceivedReviews(userId);

    if (authUser?.uid) {
      void fetchReviewableConversations(authUser.uid, conversations);
    }
  }, [
    isOpen,
    isReviewsViewOpen,
    userId,
    authUser?.uid,
    conversations,
    fetchReceivedReviews,
    fetchReviewableConversations,
  ]);

  const handleStartChat = useCallback(async () => {
    if (!authUser?.uid || !userId || isOwnProfile || !canStartChat) return;

    try {
      setIsStartingChat(true);
      const conversationId = await findOrCreateConversation(authUser.uid, userId);
      trackEvent('message_start', {
        source: 'profile',
        viewer_role: viewerRole || 'unknown',
        target_role: profile?.role || 'unknown',
      });
      onClose();

      if (onOpenConversation) {
        onOpenConversation(conversationId);
      } else {
        navigate('/messages', { state: { openConversationId: conversationId } });
      }
    } catch {
      addToast('error', 'メッセージを開始できませんでした');
    } finally {
      setIsStartingChat(false);
    }
  }, [authUser?.uid, userId, isOwnProfile, canStartChat, findOrCreateConversation, viewerRole, profile?.role, onClose, onOpenConversation, navigate, addToast]);

  const handleSendRequest = useCallback(async () => {
    if (!authUser?.uid || !userId || !isRequestButtonEnabled) return;

    try {
      setIsSubmittingRequest(true);
      await sendRequest(authUser.uid, userId, requestMessage.trim());
      const status = await checkRequestStatus(authUser.uid, userId);
      setRequestStatus(status);
      if (status === 'pending') {
        trackEvent('profile_request_start', {
          source: 'profile',
          viewer_role: viewerRole || 'unknown',
          target_role: profile?.role || 'unknown',
          has_message: requestMessage.trim().length > 0,
        });
        addToast('success', '閲覧申請を送信しました');
      } else {
        addToast('warning', '申請状況を確認してください');
      }
      setIsRequestModalOpen(false);
      setRequestMessage('');
    } catch {
      addToast('error', '閲覧申請の送信に失敗しました');
    } finally {
      setIsSubmittingRequest(false);
    }
  }, [authUser?.uid, userId, isRequestButtonEnabled, sendRequest, checkRequestStatus, requestMessage, viewerRole, profile?.role, addToast]);

  const handleEditProfile = useCallback(() => {
    onClose();
    navigate('/mypage/edit');
  }, [onClose, navigate]);

  const handleOpenReviews = useCallback(() => {
    if (!userId) return;
    if (isOwnProfile) {
      onClose();
      navigate('/mypage/reviews');
      return;
    }
    setIsReviewsViewOpen(true);
  }, [userId, isOwnProfile, onClose, navigate]);

  const handleOpenReviewComposer = useCallback(() => {
    if (!canWriteReviewForTarget || !reviewableConversationForTarget) return;
    setSelectedReviewConversation(reviewableConversationForTarget);
    setReviewRating(5);
    setReviewComment('');
    clearReviewError();
    setIsReviewComposerOpen(true);
  }, [canWriteReviewForTarget, reviewableConversationForTarget, clearReviewError]);

  const handleCloseReviewComposer = useCallback(() => {
    setIsReviewComposerOpen(false);
    setSelectedReviewConversation(null);
    setReviewComment('');
    setReviewRating(5);
    clearReviewError();
  }, [clearReviewError]);

  const handleSubmitReview = useCallback(async () => {
    if (!authUser?.uid || !userId || !selectedReviewConversation) return;
    if (isDoneeWithoutIdentity) {
      addToast('warning', 'レビュー投稿には本人確認が必要です');
      return;
    }

    try {
      await submitReview({
        fromUserId: authUser.uid,
        toUserId: selectedReviewConversation.otherUserId,
        conversationId: selectedReviewConversation.conversationId,
        rating: reviewRating,
        comment: reviewComment.trim(),
      });
      addToast('success', 'レビューを投稿しました');
      handleCloseReviewComposer();
      await fetchReceivedReviews(userId);
      await fetchReviewableConversations(authUser.uid, conversations);
    } catch (submitError) {
      const message =
        submitError instanceof Error && submitError.message === 'already-reviewed'
          ? 'この会話のレビューは投稿済みです'
          : 'レビューの投稿に失敗しました';
      addToast('error', message);
    }
  }, [
    authUser?.uid,
    userId,
    selectedReviewConversation,
    isDoneeWithoutIdentity,
    submitReview,
    reviewRating,
    reviewComment,
    addToast,
    handleCloseReviewComposer,
    fetchReceivedReviews,
    fetchReviewableConversations,
    conversations,
  ]);

  const requestButtonLabel = useMemo(() => {
    if (isCheckingRequest) return '確認中...';
    if (requestStatus === 'pending') return '閲覧申請中';
    if (requestStatus === 'approved') return '閲覧許可済み';
    if (requestStatus === 'rejected') return '再申請';
    return '閲覧申請';
  }, [isCheckingRequest, requestStatus]);

  const renderFooterActions = () => {
    if (isOwnProfile) {
      return (
        <Button
          onClick={handleEditProfile}
          fullWidth
          leftIcon={<UserRound className="h-4 w-4" />}
          className="!h-12"
        >
          プロフィールを編集する
        </Button>
      );
    }

    return (
      <div className="flex gap-2">
        <Button
          onClick={handleStartChat}
          disabled={!canStartChat}
          isLoading={isStartingChat}
          fullWidth={!showRequestButton}
          className={showRequestButton ? 'flex-1 !h-12' : '!h-12'}
          leftIcon={<MessageCircle className="h-4 w-4" />}
        >
          {canStartChat ? 'メッセージ' : 'メッセージ不可'}
        </Button>
        {showRequestButton && (
          <Button
            variant="secondary"
            className="!h-12 flex-1 !border-[#DEDBD6] !bg-white !text-[#1A1A1A]"
            disabled={!isRequestButtonEnabled}
            onClick={() => setIsRequestModalOpen(true)}
            leftIcon={<Eye className="h-4 w-4" />}
          >
            {requestButtonLabel}
          </Button>
        )}
      </div>
    );
  };

  const renderReviewStars = (value: number, size: 'sm' | 'lg' = 'sm') => (
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }).map((_, index) => {
        const starNumber = index + 1;
        return (
          <Star
            key={starNumber}
            className={`${size === 'lg' ? 'h-6 w-6' : 'h-4 w-4'} ${
              starNumber <= value ? 'fill-copper text-copper' : 'text-neutral-300'
            }`}
          />
        );
      })}
    </div>
  );

  const handleCloseSheet = useCallback(() => {
    if (isReviewsViewOpen) {
      setIsReviewsViewOpen(false);
      return;
    }
    onClose();
  }, [isReviewsViewOpen, onClose]);

  const metaParts = [
    age !== null ? `${age}歳` : null,
    profile?.physicalInfo?.height ? `${profile.physicalInfo.height}cm` : null,
    profile?.physicalInfo?.weight ? `${profile.physicalInfo.weight}kg` : null,
    profile?.basicInfo?.residence ?? null,
  ].filter((value): value is string => Boolean(value));
  const reviewHeaderMetaParts = [
    age !== null ? `${age}歳` : 'ー歳',
    profile?.basicInfo?.residence || null,
    profile?.physicalInfo?.height ? `${profile.physicalInfo.height}cm` : 'ーcm',
  ].filter((value): value is string => Boolean(value));

  return (
    <>
      <BottomSheet
        isOpen={isOpen}
        onClose={handleCloseSheet}
        height="fixed"
        showHandle={true}
        showCloseButton={true}
        title={isReviewsViewOpen ? 'レビュー' : 'プロフィール'}
        mobileFullScreen={true}
      >
        <div className="min-h-full bg-[#F7F4EF]">
          <div className={`space-y-4 px-4 pb-28 pt-2 ${isReviewsViewOpen ? 'hidden' : ''}`}>
            {isLoading && (
              <div className="flex justify-center py-10">
                <Loading />
              </div>
            )}

            {!isLoading && error && (
              <Card className="border-red-200">
                <CardContent className="py-8 text-center">
                  <p className="text-red-600">{error}</p>
                </CardContent>
              </Card>
            )}

            {!isLoading && !error && profile && (
              <>
                <Card className="overflow-hidden">
                  <CardContent className="px-4 py-4">
                    <div className="mb-3 flex items-start gap-4">
                      <Avatar
                        src={profile.iconUrl}
                        alt={profile.displayName}
                        size="lg"
                        className="h-20 w-20 rounded-2xl bg-[#D4C5A9]"
                      />
                      <div className="min-w-0 flex-1">
                        <h2 className="truncate text-[24px] font-bold leading-tight text-[#1A1A1A]">
                          {profile.displayName}
                        </h2>
                        {metaParts.length > 0 && (
                          <p className="mt-1 text-[13px] text-[#888]">{metaParts.join(' · ')}</p>
                        )}

                        {/* レビュー情報 + レビュー画面への動線 */}
                        <button
                          type="button"
                          className="group mt-2 flex items-center gap-1.5"
                          onClick={handleOpenReviews}
                        >
                          {reviewStats.totalCount > 0 ? (
                            <>
                              <Star className="h-4 w-4 fill-[#B87333] text-[#B87333]" />
                              <span className="text-sm font-semibold text-[#B87333]">
                                {reviewStats.averageRating}
                              </span>
                              <span className="text-[12px] text-[#AAA]">
                                ({reviewStats.totalCount}件)
                              </span>
                            </>
                          ) : (
                            <span className="text-[12px] text-[#AAA]">レビューなし</span>
                          )}
                          <ChevronRight className="h-4 w-4 text-[#C8C4BC] transition-colors group-hover:text-copper" />
                        </button>

                      </div>
                    </div>

                    {/* 認証状態バナー */}
                    <div className="mb-4">
                      {profile.identityVerified ? (
                        <div className="flex items-center gap-2 rounded-xl border border-[#E0C9A8] bg-[#F5EDE2] px-3 py-2.5">
                          <ShieldCheck className="h-5 w-5 flex-shrink-0 text-[#B87333]" />
                          <div>
                            <p className="text-sm font-bold text-[#B87333]">本人認証済み</p>
                            <p className="text-xs text-[#A46A36]">本人確認書類による認証が完了しています</p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 rounded-xl border border-[#E4DFD6] bg-[#F0EDE8] px-3 py-2.5">
                          <ShieldAlert className="h-5 w-5 flex-shrink-0 text-[#A89D8C]" />
                          <div>
                            <p className="text-sm font-bold text-[#7F7566]">未認証</p>
                            <p className="text-xs text-[#9A9184]">本人確認がまだ完了していません</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* 認証バッジ一覧 */}
                    {profile.role === 'donor' && (
                      <div>
                        <h3 className="mb-2 text-[12px] font-medium uppercase tracking-wider text-[#999]">認証状況</h3>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            { label: '学歴証明', ok: profile.verificationBadges?.education },
                            { label: '年収証明', ok: profile.verificationBadges?.income },
                            {
                              label: profile.verificationBadges?.ranks?.spermTest
                                ? `精液検査 (${profile.verificationBadges.ranks.spermTest})`
                                : '精液検査',
                              ok: profile.verificationBadges?.spermTest,
                            },
                            { label: '性病検査', ok: profile.verificationBadges?.stdTest },
                          ].map((item) => (
                            <div
                              key={item.label}
                              className="flex items-center gap-2 rounded-lg bg-[#F7F4EF] px-2.5 py-2"
                            >
                              <span
                                className="inline-block h-2 w-2 rounded-full"
                                style={{ backgroundColor: item.ok ? '#B87333' : '#DEDBD6' }}
                              />
                              <span
                                className="text-[12px]"
                                style={{ color: item.ok ? '#B87333' : '#A9A39A', fontWeight: item.ok ? 500 : 400 }}
                              >
                                {item.label}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="overflow-hidden">
                  <CardContent className="px-4 py-4">
                    <h3 className="mb-1.5 text-[12px] font-medium uppercase tracking-wider text-[#999]">基本情報</h3>
                    <div className="divide-y divide-[#F0EDE8]">
                      <InfoField
                        label="年齢"
                        value={
                          profile.role === 'donee' && age !== null
                            ? formatAgeRange(age)
                            : age !== null
                            ? `${age}歳`
                            : UNSET_LABEL
                        }
                      />
                      <InfoField
                        label="居住地"
                        value={displayText(profile.basicInfo?.residence)}
                      />
                      {profile.role === 'donor' && (
                        <>
                          <InfoField label="国籍" value={displayText(profile.basicInfo?.nationality)} />
                          <InfoField
                            label="血液型"
                            value={displayBloodType(profile.basicInfo?.bloodType, profile.basicInfo?.bloodTypeRh)}
                          />
                          <InfoField label="出身地" value={displayText(profile.basicInfo?.birthplace)} />
                          <InfoField
                            label="最終学歴"
                            value={displayText(profile.basicInfo?.education?.level)}
                          />
                          <InfoField label="学校名" value={displayText(profile.basicInfo?.education?.schoolName)} />
                          <InfoField label="学部・学科" value={displayText(profile.basicInfo?.education?.faculty)} />
                          <InfoField label="卒業状況" value={displayText(profile.basicInfo?.education?.status)} />
                          <InfoField label="年収" value={displayText(profile.basicInfo?.annualIncome)} />
                          <InfoField label="身長" value={displayNumber(profile.physicalInfo?.height, 'cm')} />
                          <InfoField label="体重" value={displayNumber(profile.physicalInfo?.weight, 'kg')} />
                          <InfoField label="体型" value={displayText(profile.physicalInfo?.bodyType)} />
                          <InfoField label="髪質" value={displayText(profile.physicalInfo?.hairType)} />
                          <InfoField label="髪の色" value={displayText(profile.physicalInfo?.hairColor)} />
                          <InfoField label="まぶた" value={displayText(profile.physicalInfo?.eyelid)} />
                          <InfoField
                            label="婚姻状況"
                            value={displayText(profile.basicInfo?.maritalStatus)}
                          />
                          <InfoField label="血統" value={displayText(profile.basicInfo?.ancestry)} />
                          <InfoField
                            label="子供"
                            value={
                              profile.basicInfo?.children
                                ? `息子${profile.basicInfo.children.sons}人、娘${profile.basicInfo.children.daughters}人`
                                : UNSET_LABEL
                            }
                          />
                          <InfoField
                            label="精液検査結果"
                            value={
                              profile.verificationBadges?.spermTest
                                ? `認証済み${profile.verificationBadges.ranks?.spermTest ? ` (${profile.verificationBadges.ranks.spermTest})` : ''}`
                                : UNSET_LABEL
                            }
                          />
                          <InfoField
                            label="性病検査結果"
                            value={profile.verificationBadges?.stdTest ? '陰性確認済み' : UNSET_LABEL}
                          />
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {profile.role === 'donor' && (
                  <Card className="overflow-hidden">
                    <CardContent className="px-4 py-4">
                      <h3 className="mb-1.5 text-[12px] font-medium uppercase tracking-wider text-[#999]">提供方針</h3>
                      <div className="divide-y divide-[#F0EDE8] text-sm">
                        <InfoLine
                          label="提供方法"
                          value={
                            profile.donationPolicy?.methods && profile.donationPolicy.methods.length > 0
                              ? profile.donationPolicy.methods.map(getDonationMethodLabel).join(', ')
                              : UNSET_LABEL
                          }
                        />
                        <InfoLine
                          label="提供地域"
                          value={
                            profile.donationPolicy?.donationAreas && profile.donationPolicy.donationAreas.length > 0
                              ? profile.donationPolicy.donationAreas.join(', ')
                              : UNSET_LABEL
                          }
                        />
                        <InfoLine
                          label="謝礼金"
                          value={
                            typeof profile.donationPolicy?.compensation === 'number'
                              ? `${profile.donationPolicy.compensation.toLocaleString()}円`
                              : UNSET_LABEL
                          }
                        />
                        <InfoLine label="交通費" value={displayText(profile.donationPolicy?.travelExpense)} />
                        <InfoLine label="身元開示" value={displayText(profile.donationPolicy?.identityDisclosure)} />
                        <InfoLine label="近親交配対策" value={displayText(profile.donationPolicy?.consanguinityMeasures)} />
                        <InfoLine label="出自を知る権利への対応" value={displayText(profile.donationPolicy?.rightToKnowOrigin)} />
                        <InfoLine label="同市家族への提供" value={displayText(profile.donationPolicy?.sameCityFamilyDonation)} />
                      </div>
                    </CardContent>
                  </Card>
                )}

                {profile.role === 'donee' && (
                  <Card className="overflow-hidden">
                    <CardContent className="px-4 py-4">
                      <h3 className="mb-1.5 text-[12px] font-medium uppercase tracking-wider text-[#999]">依頼方針</h3>
                      <div className="divide-y divide-[#F0EDE8] text-sm">
                        <InfoLine
                          label="希望提供方法"
                          value={
                            profile.doneePolicy?.methods && profile.doneePolicy.methods.length > 0
                              ? profile.doneePolicy.methods.map(getDonationMethodLabel).join(', ')
                              : UNSET_LABEL
                          }
                        />
                        <InfoLine
                          label="提供地域"
                          value={displayText(profile.doneePolicy?.preferredLocation)}
                        />
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card className="overflow-hidden">
                  <CardContent className="px-4 py-4">
                    <h3 className="mb-1.5 text-[12px] font-medium uppercase tracking-wider text-[#999]">自己紹介</h3>
                    <p className={`whitespace-pre-wrap text-[14px] leading-relaxed ${profile.freeText ? 'text-[#555]' : 'text-neutral-400'}`}>
                      {displayText(profile.freeText)}
                    </p>
                  </CardContent>
                </Card>

                <Card className="overflow-hidden">
                  <CardContent className="px-4 py-4">
                    <h3 className="mb-1.5 text-[12px] font-medium uppercase tracking-wider text-[#999]">追加画像</h3>
                    {profile.images.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2.5">
                        {profile.images.map((imageUrl, index) => (
                          <img
                            key={`${imageUrl}-${index}`}
                            src={imageUrl}
                            alt={`プロフィール画像 ${index + 1}`}
                            className="aspect-square w-full rounded-xl object-cover"
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="text-[13px] text-[#999]">
                        {!isOwnProfile && profile.role === 'donor'
                          ? '追加画像は閲覧申請が承認されると表示されます。'
                          : '追加画像は未設定です。'}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          {isReviewsViewOpen && (
            <div className="space-y-4 px-4 pb-28 pt-2">
              {profile && (
                <Card className="overflow-hidden">
                  <CardContent className="flex items-center gap-3 py-4">
                    <Avatar
                      src={profile.iconUrl}
                      alt={profile.displayName}
                      size="md"
                      className="h-[52px] w-[52px] rounded-2xl bg-[#D4C5A9]"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-bold text-luxury-dark">{profile.displayName}</p>
                      {reviewHeaderMetaParts.length > 0 && (
                        <p className="mt-1 text-[12px] text-[#888]">
                          {reviewHeaderMetaParts.join(' · ')}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {isLoadingReceivedReviews ? (
                <div className="py-8">
                  <Loading />
                </div>
              ) : (
                <>
                  <Card className="overflow-hidden">
                    <CardContent className="py-5">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="mb-1 text-sm text-neutral-500">総合評価</p>
                          <div className="flex items-end gap-2">
                            <span className="text-3xl font-bold text-luxury-dark">
                              {receivedStats.averageRating.toFixed(1)}
                            </span>
                            <span className="pb-1 text-sm text-neutral-500">/ 5.0</span>
                          </div>
                          <p className="mt-1 text-sm text-neutral-500">
                            {receivedStats.totalCount}件のレビュー
                          </p>
                        </div>
                        {renderReviewStars(Math.round(receivedStats.averageRating), 'lg')}
                      </div>

                      <div className="mt-4 space-y-2">
                        {distributionRows.map((star) => {
                          const count = receivedStats.distribution[star];
                          const ratio =
                            receivedStats.totalCount > 0
                              ? (count / receivedStats.totalCount) * 100
                              : 0;
                          return (
                            <div key={star} className="flex items-center gap-3">
                              <span className="w-6 text-sm text-neutral-600">{star}</span>
                              <Star className="h-3 w-3 fill-copper text-copper" />
                              <div className="h-2 flex-1 overflow-hidden rounded-full bg-neutral-100">
                                <div className="h-full bg-copper/70" style={{ width: `${ratio}%` }} />
                              </div>
                              <span className="w-8 text-right text-xs text-neutral-500">{count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  {receivedReviews.length === 0 ? (
                    <Card className="overflow-hidden">
                      <CardContent className="py-12 text-center">
                        <Star className="mx-auto mb-4 h-12 w-12 text-neutral-300" />
                        <p className="text-neutral-600">まだレビューはありません</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-3">
                      {receivedReviews.map((review) => (
                        <Card key={review.reviewId} className="overflow-hidden">
                          <CardContent className="py-4">
                            <div className="flex items-start gap-3">
                              <Avatar
                                src={review.fromUser?.iconUrl}
                                alt={review.fromUser?.displayName || 'ユーザー'}
                                size="sm"
                              />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-2">
                                  <div>
                                    <p className="font-medium text-luxury-dark">
                                      {review.fromUser?.displayName || '匿名ユーザー'}
                                    </p>
                                    <div className="mt-1">{renderReviewStars(review.rating)}</div>
                                  </div>
                                  <span className="text-xs text-neutral-500">
                                    {new Intl.DateTimeFormat('ja-JP', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                    }).format(review.createdAt)}
                                  </span>
                                </div>
                                {review.comment && (
                                  <p className="mt-2 whitespace-pre-wrap text-sm text-neutral-700">
                                    {review.comment}
                                  </p>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {!isLoading && !error && profile && !isReviewsViewOpen && (
            <div
              className="sticky bottom-0 border-t border-[#EDEBE6] bg-[#F7F4EF] px-4 pt-3"
              style={{ paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom, 0px))' }}
            >
              {renderFooterActions()}
            </div>
          )}

          {!isLoading && !error && profile && isReviewsViewOpen && (
            <div
              className="sticky bottom-0 border-t border-[#EDEBE6] bg-[#F7F4EF] px-4 pt-3"
              style={{ paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom, 0px))' }}
            >
              <Button
                onClick={handleOpenReviewComposer}
                disabled={!canWriteReviewForTarget || isSubmittingReview || isLoadingReviewableConversations}
                fullWidth
                className="!h-12"
              >
                {reviewWriteButtonLabel}
              </Button>
            </div>
          )}
        </div>
      </BottomSheet>

      <ReviewComposerBottomSheet
        isOpen={isReviewComposerOpen}
        onClose={handleCloseReviewComposer}
        onSubmit={handleSubmitReview}
        isSubmitting={isSubmittingReview}
        error={reviewError}
        targetUser={
          selectedReviewConversation
            ? {
                displayName: selectedReviewConversation.displayName,
                iconUrl: selectedReviewConversation.iconUrl,
              }
            : profile
              ? { displayName: profile.displayName, iconUrl: profile.iconUrl }
              : null
        }
        rating={reviewRating}
        onRatingChange={setReviewRating}
        comment={reviewComment}
        onCommentChange={setReviewComment}
        maxCommentLength={500}
      />

      <Modal
        isOpen={isRequestModalOpen}
        onClose={() => setIsRequestModalOpen(false)}
        title="追加画像の閲覧申請"
      >
        <div className="space-y-4">
          <p className="text-neutral-600">
            追加画像を閲覧するための申請メッセージを送信します。
          </p>
          <Textarea
            label="メッセージ（任意）"
            placeholder="自己紹介やリクエスト内容を記載してください..."
            value={requestMessage}
            onChange={(event) => setRequestMessage(event.target.value)}
            rows={4}
          />
          <div className="flex gap-2">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => setIsRequestModalOpen(false)}
            >
              キャンセル
            </Button>
            <Button
              className="flex-1"
              onClick={handleSendRequest}
              isLoading={isSubmittingRequest}
            >
              申請を送信
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

function InfoField({ label, value }: { label: string; value: string }) {
  const isUnset = value === UNSET_LABEL;
  return (
    <div className="flex items-center justify-between gap-3 py-3.5">
      <p className="text-[14px] text-[#888]">{label}</p>
      <p className={`text-[14px] font-medium text-right ${isUnset ? 'text-neutral-400' : 'text-[#1A1A1A]'}`}>{value}</p>
    </div>
  );
}

function InfoLine({ label, value }: { label: string; value: string }) {
  const isUnset = value === UNSET_LABEL;
  return (
    <div className="flex items-center justify-between gap-3 py-3.5">
      <p className="text-[14px] text-[#888]">{label}</p>
      <p className={`text-[14px] font-medium text-right ${isUnset ? 'text-neutral-400' : 'text-[#1A1A1A]'}`}>{value}</p>
    </div>
  );
}
